import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Ticket, Trophy, Zap, ArrowRight, Users, Coins, Clock, MessageCircle } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { connected } = useWallet();
  const [stats, setStats] = useState(null);
  const [latestDraw, setLatestDraw] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    fetchStats();
    fetchLatestDraw();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!stats?.next_draw) return;
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const drawTime = new Date(stats.next_draw).getTime();
      const diff = drawTime - now;
      
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [stats?.next_draw]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLatestDraw = async () => {
    try {
      const response = await axios.get(`${API}/draws/latest`);
      setLatestDraw(response.data);
    } catch (error) {
      console.log('No draws yet');
    }
  };

  const features = [
    {
      icon: Wallet,
      title: 'Pay with Crypto',
      description: 'Connect Phantom or Solflare wallet. No bank account needed.',
    },
    {
      icon: Ticket,
      title: 'Only $1 per Field',
      description: 'Start with just $1 USDC. One ticket could change your life.',
    },
    {
      icon: Zap,
      title: 'Instant & Transparent',
      description: 'All transactions on blockchain. 100% fair and verifiable.',
    },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sol-green/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sol-green font-mono text-sm tracking-widest uppercase mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-sol-green rounded-full animate-pulse"></span>
                World's First Crypto Lottery
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Turn <span className="text-sol-green neon-text">$1</span> into{' '}
                <span className="text-sol-purple">Millions</span>
              </h1>
              <p className="text-white/60 text-lg mb-4 max-w-lg">
                The classic 6 out of 49 lottery - now on Solana blockchain. 
                Pick 6 numbers, pay with crypto, win big.
              </p>
              <p className="text-sol-green font-bold text-xl mb-8">
                🎰 Only $1 per field. No limits. No borders.
              </p>
              
              <div className="flex flex-wrap gap-4">
                {connected ? (
                  <Link to="/play">
                    <button className="btn-primary" data-testid="play-now-btn">
                      <span className="flex items-center gap-2">
                        Play Now
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    </button>
                  </Link>
                ) : (
                  <WalletMultiButton className="!bg-sol-green !text-black !font-bold !px-8 !py-3 !rounded-lg" />
                )}
                <Link to="/results">
                  <button className="btn-secondary" data-testid="view-results-btn">
                    View Results
                  </button>
                </Link>
              </div>
            </motion.div>

            {/* Right Content - Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="card-glass rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-sol-green/10 rounded-full blur-2xl" />
                
                <h3 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                  <Coins className="w-6 h-6 text-sol-green" />
                  Jackpot
                </h3>
                
                <div className="mb-8">
                  <p className="text-5xl font-heading font-bold text-sol-green neon-text">
                    {stats?.jackpot_pool?.toFixed(2) || '0.00'} USDC
                  </p>
                  <p className="text-white/40 text-sm mt-2">
                    Current Jackpot
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-heading font-bold">{stats?.total_tickets || 0}</p>
                    <p className="text-white/40 text-xs">Tickets</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-bold">{stats?.total_draws || 0}</p>
                    <p className="text-white/40 text-xs">Draws</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-heading font-bold">{stats?.total_users || 0}</p>
                    <p className="text-white/40 text-xs">Players</p>
                  </div>
                </div>

                {latestDraw && (
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-white/40 text-xs mb-3">Last Winning Numbers</p>
                    <div className="flex gap-2 justify-center">
                      {latestDraw.winning_numbers.map((num) => (
                        <div key={num} className="lotto-ball lotto-ball-selected text-sm">
                          {num}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Draw Countdown */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 justify-center mb-3">
                    <Clock className="w-4 h-4 text-sol-purple" />
                    <p className="text-white/40 text-xs">Next Draw</p>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: countdown.days, label: 'Days' },
                      { value: countdown.hours, label: 'Hrs' },
                      { value: countdown.minutes, label: 'Min' },
                      { value: countdown.seconds, label: 'Sec' },
                    ].map((item) => (
                      <div key={item.label} className="text-center">
                        <div className="bg-sol-purple/20 rounded-lg py-2 px-1">
                          <span className="font-mono text-xl font-bold text-sol-purple">
                            {String(item.value).padStart(2, '0')}
                          </span>
                        </div>
                        <span className="text-white/40 text-xs">{item.label}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/40 text-xs text-center mt-2">
                    Wed & Sat at 10:00 PM
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#0F172A]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Why <span className="text-white">SOL</span><span className="text-sol-green">ottery</span>?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The world's first decentralized lottery on Solana. Fair, transparent, and accessible to everyone.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-neon rounded-xl p-8 hover:-translate-y-2 transition-transform duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-sol-green/10 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-sol-green" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/60">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Big Numbers Section */}
      <section className="py-16 bg-gradient-to-r from-sol-purple/10 to-sol-green/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-5xl font-heading font-bold text-sol-green mb-2">$1</p>
              <p className="text-white/60">Minimum to play</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-5xl font-heading font-bold text-sol-purple mb-2">50%</p>
              <p className="text-white/60">Jackpot for 6 correct</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-5xl font-heading font-bold text-white mb-2">100%</p>
              <p className="text-white/60">Transparent on blockchain</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              How It Works
            </h2>
            <p className="text-white/60">Win in 4 simple steps</p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Connect Wallet', desc: 'Phantom or Solflare' },
              { step: '02', title: 'Pick 6 Numbers', desc: 'From 1 to 49' },
              { step: '03', title: 'Pay $1 per Field', desc: 'In SOL or USDC' },
              { step: '04', title: 'Win Big!', desc: 'Draws every Wed & Sat' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-heading text-5xl font-bold text-sol-green/20 mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link to="/play">
              <button className="btn-primary" data-testid="start-playing-btn">
                <span className="flex items-center gap-2">
                  Start Playing
                  <ArrowRight className="w-5 h-5" />
                </span>
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Prize Tiers Section */}
      <section className="py-24 bg-[#0F172A]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Prize Tiers
            </h2>
            <p className="text-white/60">The more numbers you match, the bigger the prize!</p>
          </motion.div>

          <div className="space-y-4">
            {[
              { match: '6 Numbers', prize: '50% of Jackpot', color: 'sol-green', emoji: '🏆' },
              { match: '5 Numbers', prize: '20% of Jackpot', color: 'sol-purple', emoji: '🥈' },
              { match: '4 Numbers', prize: '10% of Jackpot', color: 'white', emoji: '🥉' },
              { match: 'Next Jackpot', prize: '20% Rollover', color: 'sol-purple', emoji: '🔄' },
            ].map((tier, index) => (
              <motion.div
                key={tier.match}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass rounded-xl p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{tier.emoji}</span>
                  <span className="font-heading text-xl font-bold">{tier.match}</span>
                </div>
                <span className={`text-${tier.color} font-bold text-xl`}>{tier.prize}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="card-glass rounded-2xl p-12 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-sol-purple/20 to-sol-green/20" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                Ready to become a <span className="text-sol-green">millionaire</span>?
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of players worldwide. The next draw could change your life forever.
              </p>
              <Link to="/play">
                <button className="btn-primary text-lg px-10 py-4">
                  <span className="flex items-center gap-2">
                    Buy Your Ticket Now
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </button>
              </Link>
              <p className="text-white/40 text-sm mt-4">
                Draws every Wednesday & Saturday at 22:00 (Berlin)
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 bg-[#0F172A]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Join the <span className="text-sol-purple">Community</span>
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Be part of the SoLottery community! Get jackpot updates, draw announcements, and connect with other players.
            </p>
            
            <a 
              href="https://discord.gg/jeVSyTMa" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#5865F2]/30"
              data-testid="discord-join-btn"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              Join Discord Community
            </a>
            
            <p className="text-white/40 text-sm mt-6">
              💬 Chat with players • 🎉 Win announcements • 📊 Jackpot updates
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold">
                <span className="text-white">SOL</span>
                <span className="text-sol-green">ottery</span>
              </span>
              <span className="text-white/40 text-sm ml-4">© 2025</span>
            </div>
            
            <div className="flex items-center gap-6">
              <a 
                href="https://discord.gg/jeVSyTMa" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-[#5865F2] transition-colors"
                data-testid="footer-discord-link"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
              <Link to="/results" className="text-white/60 hover:text-white transition-colors text-sm">
                Results
              </Link>
              <Link to="/play" className="text-white/60 hover:text-white transition-colors text-sm">
                Play
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
